<template>
  <div class="ns-select no-border">
    <!-- custom input for handling autofill uses name and autocomplete attributes-->
    <div :class="{ 'ns-select-div': hasArrows }">
      <div
        v-if="hasArrows"
        :class="arrowClassesLeft"
        @click="reverseArrowControls ? next() : previous()"
      >
        <NsIcon icon-name="icon-circle-chevron-left" size="custom" />
      </div>

      <input
        v-model="autoCompleteValue"
        class="select-auto-fill-input"
        tabindex="-1"
        :name="name"
        :autocomplete="autocomplete"
      />
      <VSelect
        v-model="inputValue"
        v-bind="$attrs"
        background-color="gray"
        attach
        :disabled="disabled"
        :error="!!errorMessages"
        :error-messages="errorMessages"
        :items="items"
        :item-text="itemText"
        :item-value="itemValue"
        :label="label"
        :menu-props="menuProps"
        :placeholder="placeholder"
        :required="required"
        :rules="rules"
        :readonly="readOnly"
        :validate-on-blur="validateOnBlur"
        @input="selectChanged"
        @blur="$emit('blur', inputValue)"
        @focus="$emit('focus', inputValue)"
      >
        <template v-slot:append>
          <NsIcon
            v-if="!readOnly"
            icon-name="icon-chevron-down"
            height="20"
            width="20"
          />
          <span v-else />
        </template>
      </VSelect>

      <div
        v-if="hasArrows"
        :class="arrowClassesRight"
        @click="reverseArrowControls ? previous() : next()"
      >
        <NsIcon icon-name="icon-circle-chevron-right" size="custom" />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Original Author: Paul Roberts
 * Original Team: Mavericks
 */
import { VSelect } from 'vuetify/lib';
import { NsIcon } from '@nuskin/ns-icon';

export default {
  name: 'NsSelect',
  components: { NsIcon, VSelect },
  props: {
    value: {
      type: [String, Boolean, Number, Object],
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },

    items: {
      type: Array,
      default: () => [],
    },
    itemText: {
      type: String,
      default: 'text',
    },
    itemValue: {
      type: String,
      default: 'value',
    },
    validateOnBlur: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    errorMessages: {
      type: String,
      default: '',
    },
    autocomplete: {
      type: String,
      default: '',
    },
    menuProps: {
      type: [String, Object],
      default: 'auto',
    },
    rules: {
      type: Array,
      default: () => [],
    },
    /**
     * This will enable or disable left and right arrows which allow the user to
     * navigate trough the items in the select box
     */
    hasArrows: {
      type: Boolean,
      default: false,
    },
    reverseArrowControls: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inputValue: this.value,
      autoCompleteValue: null,
    };
  },
  computed: {
    arrowClassesLeft() {
      const classes = ['arrow-class', 'ns-select-arrow-left'];
      if (
        !this.reverseArrowControls
          ? this.currItemIndex <= 0
          : this.currItemIndex >= this.items.length - 1
      ) {
        classes.push('hidden-arrow');
      }
      return classes;
    },
    arrowClassesRight() {
      const classes = ['arrow-class', 'ns-select-arrow-right'];
      if (
        !this.reverseArrowControls
          ? this.currItemIndex >= this.items.length - 1
          : this.currItemIndex <= 0
      ) {
        classes.push('hidden-arrow');
      }
      return classes;
    },
    currItemIndex() {
      return this.items.findIndex((el) => {
        return (
          el === this.inputValue ||
          el.text === this.inputValue ||
          el.value === this.inputValue
        );
      });
    },
  },
  watch: {
    value(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.inputValue = this.value;
        this.autoCompleteValue = this.value;
      }
      if (!newVal) {
        this.autoCompleteValue = '';
      }
    },
    autoCompleteValue(newVal) {
      if (newVal && this.items.length > 0 && newVal !== this.inputValue) {
        const found = this.items.find((el) => {
          return el === newVal || el.text === newVal || el.value === newVal;
        });
        if (found) {
          this.value = this.inputValue = found.value ? found.value : found;
          this.selectChanged();
        }
      }
    },
  },
  methods: {
    previous() {
      if (this.currItemIndex > 0) {
        const i = this.currItemIndex - 1;
        this.inputValue = this.items[i].value
          ? this.items[i].value
          : this.items[i];
        this.selectChanged();
      }
    },
    next() {
      if (this.currItemIndex < this.items.length - 1) {
        const i = this.currItemIndex + 1;
        this.inputValue = this.items[i].value
          ? this.items[i].value
          : this.items[i];
        this.selectChanged();
      }
    },
    selectChanged() {
      this.$emit('input', this.inputValue);
    },
  },
};
</script>

<style lang="scss">
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";
@use "~@nuskin/ns-core-styles/src/scss/colors";

* {
  $color-dryink: map.get(typographyVariables.$typography, font-family-primary);
}

.hidden-arrow {
  visibility: hidden;
}

.ns-select-div {
  display: flex;
  align-items: flex-end;
  .v-messages {
    display: none;
  }

  .arrow-class {
    padding: 0 10px;
    margin-bottom: 12px;
  }
  svg {
    width: 24px;
    height: 24px;
  }
}

.ns-select {
  .v-select {
    .v-menu__content {
      left: 0 !important;
    }
  }

  // Can't use display none for the desired functionality, hide the input off the screen
  .select-auto-fill-input {
    position: fixed;
    top: 150%;
    left: 150%;
  }
}

.ns-select {
  .theme--light.v-input:not(.v-input--is-disabled):not(.ns-select-div) input {
    border: 1px solid map.get(colors.$theme, 'primary');
    border-radius: 0px;
  }

  .theme--light.v-text-field > .v-input__control > .v-input__slot:before,
  .theme--light.v-text-field > .v-input__control > .v-input__slot:hover:before {
    border-color: transparent;
  }

  .v-select-list .primary--text {
    color: white !important;
    caret-color: map.get(colors.$theme, 'primary') !important;
    background-color: map.get(colors.$theme, 'primary');
    font-weight: 600;
  }
  .v-list .v-list__tile--link:hover {
    background: map.get(colors.$theme, 'accent-color-b-100');
    color: map.get(colors.$theme, 'primary') !important;
    font-weight: 600;
  }

  .v-menu__content.menuable__content__active {
    box-shadow: none;
    border-radius: 0px;
    border: 1px solid map.get(colors.$theme, 'primary') !important;
  }

  .v-select__slot {
    align-items: stretch;
  }

  @media screen and (min-width: 480px) and (max-width: 767px) {
    .ns-select-div .arrow-class {
      margin-bottom: 18px;
    }

    .v-select__slot .v-input__append-inner {
      margin-bottom: 8px;
    }
  }

  @media screen and (max-width: 480px) {
    .ns-select-div .arrow-class {
      margin-bottom: 18px;
    }

    .v-select__slot .v-input__append-inner {
      margin-bottom: 12px;
      bottom: 0px;
    }
  }
}
</style>
